/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
    CardSection,
    CardHeader,
    AddressLink,
    ActionButton,
} from '@europrocurement/flexy-components';
import { Box, Typography, useTheme } from '@mui/material';
import {
    AdresseTiersReadNoteGroupTimestampableBlameambleGroup,
    ContactTiersReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { formatPrescriberAddress } from '@europrocurement/l2d-utils';
import { minusIcon, plusIcon } from '@europrocurement/l2d-icons';
import ClipboardLine from '../components/ClipboardLine';
import { PrescriberTabProps } from './types';
import ContactList from '../components/ContactList';

const Contacts: React.FunctionComponent<PrescriberTabProps> = function (props) {
    const { prescriber } = props;
    const { palette } = useTheme();
    const [activeContacts, setActiveContacts] = useState<
        ContactTiersReadNoteGroupTimestampableBlameambleGroup[] | []
    >([]);
    const [inactiveContacts, setInactiveContacts] = useState<
        ContactTiersReadNoteGroupTimestampableBlameambleGroup[] | []
    >([]);
    const [isListShown, setIsListShown] = useState(false);
    const [isLimitedResult, setIsLimitedResult] = useState(true);
    const mainAddress = prescriber?.adresses?.find((address: any) => address.defaut === true);

    const limit: number = 2;

    const getAddressLabel = (address?: AdresseTiersReadNoteGroupTimestampableBlameambleGroup) => {
        if (!address) {
            return '';
        }
        const isDefaultAddress = address.defaut;
        const isBillingAddress = address.facturation;
        if (isDefaultAddress && isBillingAddress) return 'Adresse Principale et de Facturation';
        if (isDefaultAddress && !isBillingAddress) return 'Adresse Principale';
        if (!isDefaultAddress && isBillingAddress) return 'Adresse de Facturation';
        return '';
    };

    const copyableLines: Array<{ label: string; value?: string | null }> = [
        {
            label: 'SIRET',
            value: prescriber.siret,
        },
        {
            label: 'SIREN',
            value: prescriber.siren,
        },
        {
            label: 'TVA Intracommunataire',
            value: prescriber.tvaIntracommunautaire,
        },
        {
            label: 'Régime TVA',
            value: prescriber.regimeTva,
        },
    ];

    const filterAndSortContacts = (
        contactsToSort: ContactTiersReadNoteGroupTimestampableBlameambleGroup[],
        inactiveContact = false,
        limitResults = false,
    ): ContactTiersReadNoteGroupTimestampableBlameambleGroup[] => {
        // fitler activ / inactiv contacts
        const filteredContacts = contactsToSort.filter(
            (contact) => contact.isExit === inactiveContact,
        );

        // Sort contacts by admin > compta > alphabetical order
        filteredContacts.sort((a, b) => {
            const aPrincipal = a.principal ? 1 : -1;
            const bPrincipal = b.principal ? 1 : -1;
            const aCompta = a.comptabilite ? 1 : -1;
            const bCompta = b.comptabilite ? -1 : -1;

            // Sort by admin > compta > alphabetical order
            return (
                bPrincipal - aPrincipal ||
                bCompta - aCompta ||
                a.nomContact.localeCompare(b.nomContact) ||
                -1
            );
        });
        if (limitResults) {
            return filteredContacts.slice(0, limit);
        }
        return filteredContacts;
    };

    const handleShowAllContacts = (listShown: boolean) => {
        setIsListShown(listShown);
        setIsLimitedResult(!listShown);
    };

    useEffect(() => {
        setActiveContacts(filterAndSortContacts(prescriber.contacts, false, isLimitedResult));
        setInactiveContacts(filterAndSortContacts(prescriber.contacts, true));
    }, [prescriber, isLimitedResult]);

    return (
        <Box sx={{ paddingRight: '5px' }}>
            <CardSection
                sx={{ marginTop: '0px' }}
                header={
                    <CardHeader
                        title="Informations générales"
                        border={false}
                    />
                }
            >
                {mainAddress && (
                    <ClipboardLine
                        label={getAddressLabel(mainAddress)}
                        value={<AddressLink address={mainAddress} />}
                        overridedValue={formatPrescriberAddress(mainAddress, 'short')}
                    />
                )}
                {copyableLines.map(({ label, value }, index) => {
                    const key = `prescriber_contacts_infoline_${index}`;
                    return (
                        <ClipboardLine
                            key={key}
                            label={label}
                            value={value}
                        />
                    );
                })}

                {prescriber.urlSocieteDotCom && (
                    <a
                        href={prescriber.urlSocieteDotCom}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'inherit' }}
                    >
                        <Typography
                            fontSize={18}
                            color={palette.primary.main}
                            sx={{ marginTop: '15px' }}
                        >
                            {'Fiche société.com >'}
                        </Typography>
                    </a>
                )}
            </CardSection>
            <ContactList
                contacts={activeContacts}
                sectionTitle="Contacts principaux"
                type="active"
                {...props}
            />
            {/* Inactiv contacts */}
            {isListShown && inactiveContacts.length > 0 && (
                <ContactList
                    contacts={inactiveContacts}
                    sectionTitle="Contacts inactifs"
                    type="inactive"
                    {...props}
                />
            )}
            {/* Button display / hide more contacts */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {prescriber.contacts && prescriber.contacts.length > limit && (
                    <ActionButton
                        action={() => handleShowAllContacts(!isListShown)}
                        label={
                            <Typography variant="l2dcaption">
                                {`Afficher ${isListShown ? 'moins' : 'plus'} de contacts`}
                            </Typography>
                        }
                        icon={isListShown ? minusIcon : plusIcon}
                        sx={{
                            marginTop: '25px',
                            backgroundColor: 'transparent',
                            color: palette.primary.main,
                            minWidth: 'fit-content',
                            transition: 'color ease-in-out 300ms',
                            ':hover': {
                                backgroundColor: 'transparent',
                                color: palette.primary.dark,
                            },
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Contacts;
